import {cn} from '@sollapay/ui';
import {Badge, Button, Icon, Popover, Typography} from '@sollapay/ui/components';
import {useTranslation} from 'react-i18next';

import {RosterPopoverTrigger} from '@/components';

import {SellersRosterPopoverContent} from './SellersRosterPopoverContent';

import type {TrustAccountListSellerDTO} from '@sollapay/types';
import type {FC} from 'react';

/** `inline` is the grid card's meta row: seller at the start, buyers at the end, each with an icon. */
export interface TrustAccountSellersCellProps {
  sellers: TrustAccountListSellerDTO[];
  buyersCount: number;
  /** Where the buyers count goes — the account's buyers tab. */
  onOpenBuyers?: () => void;
  /** False drops the buyers line, e.g. for an account still in setup. */
  showBuyers?: boolean;
  layout?: 'stacked' | 'inline';
  /** Shown when there is no seller. */
  emptyLabel?: string;
}

/** The first seller (and "+N" for the rest, which opens them all) with the buyer count under it. */
export const TrustAccountSellersCell: FC<TrustAccountSellersCellProps> = ({
  sellers,
  buyersCount,
  onOpenBuyers,
  showBuyers = true,
  layout = 'stacked',
  emptyLabel = '—'
}) => {
  const isInline = layout === 'inline';

  return (
    <div
      data-slot="sellers-cell"
      className={cn(
        'flex min-w-0',
        isInline ? 'items-center justify-between gap-3' : 'flex-col gap-0.5'
      )}
    >
      <SellerName sellers={sellers} emptyLabel={emptyLabel} withIcon={isInline} />
      {showBuyers && <BuyersCount count={buyersCount} onOpen={onOpenBuyers} />}
    </div>
  );
};

interface SellerNameProps {
  sellers: TrustAccountListSellerDTO[];
  emptyLabel: string;
  withIcon: boolean;
}

const SellerName: FC<SellerNameProps> = ({sellers, emptyLabel, withIcon}) => {
  const firstSeller = sellers[0];
  const name = firstSeller?.fullName || emptyLabel;

  return (
    <div className={cn('flex items-center gap-2', withIcon && 'min-w-0 gap-1.5')}>
      {withIcon && <Icon name="user" aria-hidden className="size-3.75 shrink-0 text-fg-disabled" />}
      <Typography
        size="sm"
        weight="medium"
        color={firstSeller ? (withIcon ? 'secondary' : 'default') : 'tertiary'}
        className="min-w-0 truncate"
        title={firstSeller?.fullName ?? undefined}
      >
        {name}
      </Typography>
      {sellers.length > 1 && <OtherSellers sellers={sellers} />}
    </div>
  );
};

interface OtherSellersProps {
  sellers: TrustAccountListSellerDTO[];
}

const OtherSellers: FC<OtherSellersProps> = ({sellers}) => {
  const {t} = useTranslation('trustAccounts');

  return (
    <Popover>
      <RosterPopoverTrigger
        overRowLink
        className="min-w-0"
        ariaLabel={`+${sellers.length - 1} ${t('view.header.sellersPopover.title')}`}
      >
        <Badge size="sm" dir="ltr">{`+${sellers.length - 1}`}</Badge>
      </RosterPopoverTrigger>
      <SellersRosterPopoverContent
        sellers={sellers}
        title={t('view.header.sellersPopover.title')}
      />
    </Popover>
  );
};

interface BuyersCountProps {
  count: number;
  onOpen?: () => void;
}

const BuyersCount: FC<BuyersCountProps> = ({count, onOpen}) => {
  const {t} = useTranslation('trustAccounts');
  const icon = <Icon name="users" className="size-3.5 shrink-0" />;

  if (count === 0 || !onOpen) {
    return (
      <Typography size="xs" color="tertiary" className="flex items-center gap-1">
        {icon}
        {count === 0 ? t('columns.noBuyers') : t('columns.buyersCount', {count})}
      </Typography>
    );
  }

  return (
    <Button
      variant="linkColor"
      className="relative z-10 h-auto w-fit gap-1 p-0 text-xs"
      onClick={event => {
        event.stopPropagation();
        onOpen();
      }}
    >
      {icon}
      {t('columns.buyersCount', {count})}
    </Button>
  );
};
